// =============================================================================
// Centralne formattery — Pakiet 1 (2026-02-24)
//
// Ujednolicone helpery używane w całej aplikacji do formatowania dat i kwot.
// Cel:
//   - eliminacja "Invalid Date" — bezpieczny fallback dla pustych/błędnych wartości
//   - jeden formatter kwoty w miejsce 4-5 lokalnych kopii (żaden nie duplikuje PLN)
//   - jednolita konwencja locale pl-PL
//
// Referencja: /app/docs/DESKTOP_UX_PACKAGE1_NO_HORIZONTAL_SCROLL.md
// =============================================================================

const PL = "pl-PL";

/**
 * Bezpieczne parsowanie wartości do Date.
 * @returns {Date | null} — null, gdy wartość jest pusta / nieparsowalna.
 */
const parseDateSafe = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Formatuje datę w skróconym polskim formacie (dd.mm.rrrr).
 * Zwraca `fallback` gdy wartość jest pusta lub nieparsowalna (nigdy „Invalid Date").
 *
 * @param {string | Date | null | undefined} value
 * @param {string} fallback — domyślnie "—". Dla terminów płatności użyj `"Brak terminu"`.
 */
export const formatDate = (value, fallback = "—") => {
  const d = parseDateSafe(value);
  if (!d) return fallback;
  return d.toLocaleDateString(PL, { year: "numeric", month: "2-digit", day: "2-digit" });
};

/**
 * Wariant dla terminów płatności — czytelny komunikat zamiast myślnika.
 */
export const formatDueDate = (value) => formatDate(value, "Brak terminu");

/**
 * Formatuje datę + czas (dd.mm.rrrr, HH:MM).
 */
export const formatDateTime = (value, fallback = "—") => {
  const d = parseDateSafe(value);
  if (!d) return fallback;
  const date = d.toLocaleDateString(PL, { year: "numeric", month: "2-digit", day: "2-digit" });
  const time = d.toLocaleTimeString(PL, { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
};

/**
 * Formatuje miesiąc księgowy ("luty 2026").
 */
export const formatMonth = (value, fallback = "—") => {
  const d = parseDateSafe(value);
  if (!d) return fallback;
  return d.toLocaleDateString(PL, { year: "numeric", month: "long" });
};

/**
 * Formatuje kwotę pieniężną w konwencji pl-PL z jednokrotnym sufiksem waluty.
 *
 * UWAGA: nie używa `Intl.NumberFormat(currency: ...)` bo ta zawsze dokleja
 * kod waluty (nawet gdy `currency` bywa nullem) — używamy ręcznego sufiksu.
 * Gwarantuje BRAK podwójnego „PLN PLN" gdy input już go zawiera.
 *
 * @param {number | string | null | undefined} amount
 * @param {string} currency — domyślnie "PLN"
 */
export const formatAmount = (amount, currency = "PLN") => {
  const n = Number(amount ?? 0);
  const safe = isNaN(n) ? 0 : n;
  const formatted = safe.toLocaleString(PL, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Gdy jawnie przekażemy `""`, `null` lub `false` — nie doklejamy waluty.
  // Domyślny argument `"PLN"` obowiązuje tylko gdy użytkownik pominie parametr.
  if (currency === "" || currency === null || currency === false) {
    return formatted;
  }
  const cur = currency.toString().trim().toUpperCase();
  return `${formatted} ${cur}`;
};

/**
 * Alias dla `formatAmount` — używamy tej nazwy w wielu miejscach (`formatMoney`).
 * Wystawiamy oba, żeby refaktor nie wymagał przemianowań w każdym miejscu.
 */
export const formatMoney = formatAmount;
